import React, { useEffect } from 'react'
import dayjs from 'dayjs'
import { snakeCase } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { Box, DataListItem } from '@node-space/ui-components'
import { ChannelPayment } from 'types/channels'
import {
  AmplitudeEvent,
  AmplitudeEventAction,
  AmplitudeEventCategory,
} from 'utils/amplitude/amplitudeEvents'
import track from 'utils/tracker'
import { shortenString } from 'utils/utils'

interface ChannelDetailsProps {
  channelPayment: ChannelPayment
}

const ChannelDetails = ({ channelPayment }: ChannelDetailsProps) => {
  const { t } = useTranslation()

  useEffect(() => {
    track.Amp.track(AmplitudeEvent.CHANNEL_VIEW_DETAILS, {
      category: AmplitudeEventCategory.MERCHANT_PORTAL,
      action: AmplitudeEventAction.LOAD,
    })
  }, [])

  const dataListItems = [
    <DataListItem
      id="amountIn"
      key="amountIn"
      testId={`details-${snakeCase(channelPayment?.reference)}-amount`}
      title={t('payments.channels.amountIn')}
      body={`${channelPayment?.paidAmount} ${channelPayment?.paidCurrency}`}
      layout={'horizontal'}
    />,
    <DataListItem
      id="feeAmount"
      key="feeAmount"
      testId={`details-${snakeCase(channelPayment?.reference)}-fee`}
      title={t('payments.channels.feeAmount')}
      body={`${channelPayment?.feeAmount} ${channelPayment?.feeCurrency}`}
      layout={'horizontal'}
    />,
    <DataListItem
      id="walletCurrency"
      key="walletCurrency"
      testId={`details-${snakeCase(channelPayment?.reference)}-wallet-currency`}
      title={t('payments.channels.walletCurrency')}
      body={channelPayment?.walletCurrency}
      layout={'horizontal'}
    />,
    <DataListItem
      id="displayCurrency"
      key="displayCurrency"
      testId={`details-${snakeCase(channelPayment?.reference)}-display-currency`}
      title={t('payments.channels.displayCurrency')}
      body={channelPayment?.walletCurrency}
      layout={'horizontal'}
    />,
    <DataListItem
      id="exchangeRate"
      key="exchangeRate"
      testId={`details-${snakeCase(channelPayment?.reference)}-exchange-rate`}
      title={t('payments.channels.exchangeRate')}
      body={channelPayment?.exchangeRate?.rate?.toString()}
      layout={'horizontal'}
    />,
    <DataListItem
      id="createdDate"
      key="createdDate"
      testId={`details-${snakeCase(channelPayment?.reference)}-date-created`}
      title={t('createdDate')}
      body={dayjs(channelPayment?.dateCreated).format('YYYY-MM-DD, HH:mm:ss')}
      layout={'horizontal'}
    />,
    <DataListItem
      id="reference"
      key="reference"
      testId={`details-${snakeCase(channelPayment?.reference)}-reference`}
      title={t('reference')}
      body={channelPayment?.reference}
      copyText={channelPayment?.reference}
      layout={'horizontal'}
    />,
    <DataListItem
      id="status"
      key="status"
      testId={`details-${snakeCase(channelPayment?.reference)}-status`}
      title={t('status')}
      body={channelPayment?.status}
      layout={'horizontal'}
    />,
    <DataListItem
      id="uuid"
      key="uuid"
      testId={`details-${snakeCase(channelPayment?.reference)}-uuid`}
      title={t('Uuid')}
      body={shortenString(channelPayment?.uuid, 20)}
      copyText={channelPayment?.uuid}
      layout={'horizontal'}
    />,
  ]

  return (
    <Box flex flexCol width="full">
      {dataListItems}
    </Box>
  )
}

export default React.memo(ChannelDetails)
